import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Handler from "../../Helpers/Handler";
import { formatDateTime } from "../../Helpers/Utils";
import { StatusPill } from "../../Components/Needed";
import ListLayout from "../../Components/List";
import HubFormModal from "./HubFormModal";
import useListParams from "../../Helpers/useListParams";

const INITIAL_FILTERS = { search: "", searchKey: "hubId", count: 10, status: "", hubType: "", sortBy: "createdAt", sort: false };

const SEARCH_KEYS = [{ key: "hubId", value: "Hub / address / area / city / state" }];

const FILTER_ITEMS = {
    dropdown: [
        {
            key: "status",
            name: "Status",
            options: [{ key: "", name: "All" }, { key: true, name: "Active" }, { key: false, name: "Inactive" }],
        },
        {
            key: "hubType",
            name: "Type",
            options: [{ key: "", name: "All" }, { key: "Hub", name: "Hub" }, { key: "Host", name: "Host" }],
        },
    ],
    sort: [
        { key: "hubId", name: "Hub ID" },
        { key: "area", name: "Area" },
        { key: "city", name: "City" },
        { key: "state", name: "State" },
        { key: "country", name: "Country" },
        { key: "pincode", name: "Pincode" },
        { key: "createdAt", name: "Created" },
    ],
};

const HubList = () => {
    const navigate = useNavigate();
    // Page number in the URL path, filters in the query string - see Helpers/useListParams.
    const { page, setPage, filters, setFilters } = useListParams(INITIAL_FILTERS);
    const [data, setData] = useState({ list: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        const response = await Handler({
            method: "post",
            url: `/staff/hub/list/${page - 1}`,
            params: { count: filters.count },
            data: {
                ...(filters.search ? { search: filters.search } : {}),
                ...(filters.status !== "" ? { status: filters.status } : {}),
                ...(filters.hubType ? { hubType: filters.hubType } : {}),
                sortBy: filters.sortBy,
                sort: filters.sort,
            },
        });
        if (response?.success) {
            setData(response?.data?.data || { list: [], total: 0 });
        } else {
            setData({ list: [], total: 0 });
            setError(response?.data?.message || "No data found");
        }
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, filters.count, filters.search, filters.status, filters.hubType, filters.sortBy, filters.sort]);

    useEffect(() => { load(); }, [load]);

    const onFilterChange = (key, value) => {
        if (key !== "count") setPage(1);
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const resetFilter = () => {
        setFilters(INITIAL_FILTERS);
        setPage(1);
    };

    const columns = [
        { key: "hubId", name: "Hub ID", mandatory: true },
        { key: "hubType", name: "Type" },
        { key: "address", name: "Address", render: (row) => [row.buildingNo, row.street, row.area].filter(Boolean).join(", ") },
        { key: "city", name: "City" },
        { key: "state", name: "State" },
        { key: "pincode", name: "Pincode" },
        { key: "status", name: "Status", render: (row) => <StatusPill active={row.status} /> },
        { key: "createdAt", name: "Created", render: (row) => formatDateTime(row.createdAt) },
    ];

    return (
        <>
            <ListLayout
                title="Hubs"
                description="Pickup and drop-off locations - hubs and hosts."
                data={data}
                loading={loading}
                error={error}
                onRetry={load}
                onRefresh={load}
                table={columns}
                page={page}
                setPageNo={setPage}
                pageSize={filters.count}
                onPageSizeChange={(size) => onFilterChange("count", size)}
                filters={filters}
                initialFilters={INITIAL_FILTERS}
                filterItems={FILTER_ITEMS}
                onFilterChange={onFilterChange}
                resetFilter={resetFilter}
                searchKeys={SEARCH_KEYS}
                searchPlaceholder="Search by hub id, address, area, city, state..."
                onRowClick={(row) => navigate(`/hubs/${row.hubId}`)}
                addList={() => setShowAddModal(true)}
            />

            {showAddModal ? (
                <HubFormModal
                    mode="create"
                    onClose={() => setShowAddModal(false)}
                    onSaved={() => { setShowAddModal(false); load(); }}
                />
            ) : ""}
        </>
    );
};

export default HubList;
